const content = `
# PhD API

The PhD API is a Flask backend service that handles topology simulation, the forecasting pipeline, and model management. It runs on Google Cloud Run.

## Route Groups

### Simulation

| Method | Endpoint | Description |
|--------|----------|-------------|
| POST | \`/api/simulate\` | Run a topology simulation with given equipment config |
| POST | \`/api/simulate/compare\` | Run two topology simulations in parallel for comparison |

### ComStock Data

| Method | Endpoint | Description |
|--------|----------|-------------|
| GET | \`/api/comstock/status\` | Check download status for all building types |
| POST | \`/api/comstock/download\` | Start downloading a building type from S3 |
| GET | \`/api/comstock/stats\` | Building counts and quality stats per type |

### Sampling

| Method | Endpoint | Description |
|--------|----------|-------------|
| GET | \`/api/sampling/status\` | Current sample state and configuration |
| GET | \`/api/sampling/coverage\` | Coverage matrix (building type x climate zone) |
| POST | \`/api/sampling/build\` | Build a stratified sample from downloaded data |

### Model Training

| Method | Endpoint | Description |
|--------|----------|-------------|
| GET | \`/api/models\` | List all model versions |
| GET | \`/api/models/:version\` | Get details for a specific version |
| POST | \`/api/models/train\` | Start a training job (background) |
| GET | \`/api/models/train/:jobId\` | Poll training job status |

### Validation

| Method | Endpoint | Description |
|--------|----------|-------------|
| GET | \`/api/validation\` | List all validation runs |
| POST | \`/api/validation/run\` | Start full 5-scenario validation suite |
| POST | \`/api/validation/cold-start\` | Run cold-start-only quick test |
| GET | \`/api/validation/:runId\` | Get validation run results |

### Prediction

| Method | Endpoint | Description |
|--------|----------|-------------|
| POST | \`/api/models/active/predict\` | Run cold-start prediction with active model |

## Service Layer

The API follows a service pattern where route handlers are thin and delegate to service classes:

\`\`\`
routes/forecast_routes.py    --> services/comstock_service.py
                             --> services/training_service.py
                             --> services/validation_service.py
routes/simulation_routes.py  --> services/simulation_service.py
\`\`\`

## ComStock Cache

Downloaded data and trained models are stored in a local filesystem cache:

\`\`\`
.comstock_cache/
    buildings/
        {building_type}/
            metadata.parquet    -- Building metadata for this type
            profiles.parquet    -- 8760 hourly profiles per building
    samples/
        current/
            sample.parquet      -- Stratified sample of building IDs
    models/
        {version}/
            manifest.json       -- Version metadata and metrics
            {component}/{type}/{cluster}/
                model.lgb       -- Serialized LightGBM model
                metadata.json   -- Feature importance, test scores
\`\`\`

## Background Jobs

Training and validation run as background threads. The API returns a job ID immediately, and the frontend polls for status updates. Job state is kept in-memory (not persisted across restarts).

## Deployment

Deploy with:

\`\`\`bash
./tools/deploy-phd.sh
\`\`\`

This bundles \`flux-core\` (shared library with forecasting models) into the Docker image.
`;

export default content;
